import { useState, useEffect, useRef } from "react";
import { HiMail, HiPhone, HiLocationMarker } from "react-icons/hi";
import { FaArrowUp } from "react-icons/fa";

/* ── Reveal on scroll ── */
function useFooterReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, vis];
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [footerRef, footerVis] = useFooterReveal();


  const quickLinks = [
    { href: "/", label: "Home" },
    { href: "/past", label: "Present Lutyens" },
    { href: "/vision", label: "Vision Lutyens" },
  ];

  const contacts = [
    { icon: HiLocationMarker, text: "New Delhi, India", href: null },
    { icon: HiMail, text: "info@lutyensproject.com", href: "mailto:info@lutyensproject.com" },
    { icon: HiPhone, text: "+91 123 456 7890", href: "tel:+911234567890" },
  ];

  return (
    <footer ref={footerRef} className="bg-black text-white relative overflow-hidden">
      {/* Decorative ambient */}
      <div className="absolute top-0 left-1/4 w-80 h-80 bg-yellow-900/[0.04] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-60 h-60 bg-yellow-800/[0.03] rounded-full blur-[100px] pointer-events-none" />

      {/* Gold top accent */}
      <div className="h-[3px] bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">

        {/* ═══ MAIN SECTION ═══ */}
        <div className="py-10 sm:py-14 lg:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-y-10 gap-x-6 sm:gap-x-8 lg:gap-x-12">

          {/* ── Brand Column ── */}
          <div
            className="sm:col-span-2 lg:col-span-5 transition-all duration-700"
            style={{
              transform: footerVis ? "translateY(0)" : "translateY(20px)",
              opacity: footerVis ? 1 : 0,
            }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/assets/logo.png"
                alt="The Lutyens Project"
                className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
              />
              <div className="h-8 w-px bg-yellow-600/30 flex-shrink-0" />
              <div>
                <h2
                  className="text-white font-bold text-base sm:text-lg leading-tight"
                  style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                >
                  The Lutyens
                </h2>
                <span className="text-yellow-400/60 text-[7px] sm:text-[8px] font-bold tracking-[0.35em] uppercase">
                  Project
                </span>
              </div>
            </div>

            <p className="text-white/40 text-[13px] sm:text-sm leading-relaxed max-w-sm">
              Documenting the transformation of India's capital — where
              heritage meets modern vision.
            </p>
          </div>

          {/* ── Quick Links ── */}
          <div
            className="lg:col-span-3 transition-all duration-700"
            style={{
              transitionDelay: "100ms",
              transform: footerVis ? "translateY(0)" : "translateY(20px)",
              opacity: footerVis ? 1 : 0,
            }}
          >
            <h3 className="text-white text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4 sm:mb-5 flex items-center gap-2">
              <span className="w-5 h-px bg-yellow-400" />
              Navigate
            </h3>
            <ul className="flex flex-col gap-2.5 sm:gap-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-white/40 text-[13px] sm:text-sm hover:text-white transition-all duration-300 inline-flex items-center gap-2.5 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-yellow-600 group-hover:bg-yellow-400 group-hover:shadow-[0_0_6px_rgba(250,204,21,0.4)] transition-all duration-300" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Contact ── */}
          <div
            className="lg:col-span-4 transition-all duration-700"
            style={{
              transitionDelay: "200ms",
              transform: footerVis ? "translateY(0)" : "translateY(20px)",
              opacity: footerVis ? 1 : 0,
            }}
          >
            <h3 className="text-white text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4 sm:mb-5 flex items-center gap-2">
              <span className="w-5 h-px bg-yellow-400" />
              Contact
            </h3>
            <div className="flex flex-col gap-3 sm:gap-3.5">
              {contacts.map((c) => {
                const inner = (
                  <>
                    <div className="w-8 h-8 rounded-md bg-white/[0.03] border border-white/[0.06] group-hover:border-yellow-500/25 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <c.icon className="w-3.5 h-3.5 text-yellow-400/80" />
                    </div>
                    <span className="text-white/40 text-[13px] sm:text-sm group-hover:text-white/70 transition-colors duration-300 truncate">
                      {c.text}
                    </span>
                  </>
                );
                return c.href ? (
                  <a key={c.text} href={c.href} className="flex items-center gap-3 group">
                    {inner}
                  </a>
                ) : (
                  <div key={c.text} className="flex items-center gap-3 group">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══ DIVIDER ═══ */}
        <div className="relative h-px">
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-all duration-1000"
            style={{
              transform: footerVis ? "scaleX(1)" : "scaleX(0)",
              transitionDelay: "400ms",
            }}
          />
        </div>

        {/* ═══ BOTTOM BAR ═══ */}
        <div
          className="py-5 sm:py-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 transition-all duration-700"
          style={{
            transitionDelay: "500ms",
            opacity: footerVis ? 1 : 0,
          }}
        >
          <p className="text-white/20 text-[10px] sm:text-[11px] tracking-wide">
            © {currentYear} The Lutyens Project. All rights reserved.
          </p>

          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-1.5 text-white/25 hover:text-yellow-400 text-[10px] sm:text-[11px] tracking-widest uppercase transition-colors duration-300 group"
          >
            Back to top
            <span className="w-6 h-6 rounded-full border border-white/10 group-hover:border-yellow-400/40 flex items-center justify-center transition-all duration-300 group-hover:-translate-y-0.5">
              <FaArrowUp className="w-2 h-2" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
